// Today screen — matches the wireframe:
//   - "Welcome back, {name}."
//   - YOUR PIPELINE RIGHT NOW: 5-cell stats + stacked funnel bar with stage labels
//   - "N things need you today" — up to 3 dynamic rows:
//       1. Messages waiting to send (count of leads awaiting coach approval)
//       2. New prospects found overnight (leads created in the last 24 hours)
//       3. A recent reply (latest 'replied' event, sage-highlighted)
//   - Quiet reassurance text + "Start a new campaign »" button
//
// The Start-a-new-campaign button lives INSIDE the screen (per wireframe), so
// AviaryV2Page hides the sticky footer CTA on the 'today' step.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, Center, Divider, Group, Loader, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconArrowRight, IconExternalLink } from '@tabler/icons-react';
import useApi from '../../hooks/useApi';
import { fetchCampaigns, fetchEvents, fetchLeads, fetchStats } from '../../services/api';

// Funnel bar shades — light → dark for prospect → booked.
const FUNNEL_STAGES = [
  { key: 'prospect', label: 'Prospect', color: 'var(--mantine-color-sage-1)' },
  { key: 'contacted', label: 'Contacted', color: 'var(--mantine-color-sage-3)' },
  { key: 'clicked', label: 'Clicked', color: 'var(--mantine-color-sage-5)' },
  { key: 'booked', label: 'Booked', color: 'var(--mantine-color-sage-7)' },
];

function StatCell({ label, value, highlight = false }) {
  return (
    <Stack gap={2} align="flex-start" style={{ flex: 1, minWidth: 0 }}>
      <Text
        fw={700}
        style={{
          color: highlight ? 'var(--mantine-color-sage-6)' : 'var(--aviary-ink)',
          fontSize: 34,
          lineHeight: 1,
        }}
      >
        {value}
      </Text>
      <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
        {label}
      </Text>
    </Stack>
  );
}

function FunnelBar({ counts }) {
  const total = FUNNEL_STAGES.reduce((n, s) => n + (counts[s.key] || 0), 0);
  if (total === 0) {
    return (
      <Box style={{ height: 12, borderRadius: 6, background: 'var(--aviary-disabled)' }} />
    );
  }
  return (
    <Group gap={0} wrap="nowrap" style={{ height: 12, borderRadius: 6, overflow: 'hidden' }}>
      {FUNNEL_STAGES.map((stage) => {
        const n = counts[stage.key] || 0;
        if (n === 0) return null;
        const pct = (n / total) * 100;
        return <Box key={stage.key} style={{ width: `${pct}%`, height: '100%', background: stage.color }} />;
      })}
    </Group>
  );
}

// One dynamic row on the "N things need you today" list.
// Two variants: numeric badge (default) or avatar (highlighted, for a person-triggered notification).
function ActionRow({ badge, title, subtitle, ctaLabel, onClick, highlight = false, avatar = false }) {
  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      style={{
        background: highlight ? 'var(--mantine-color-sage-0)' : '#fff',
        borderColor: highlight ? 'var(--mantine-color-sage-4)' : undefined,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <Group justify="space-between" wrap="nowrap" align="center" gap="md">
        <Group gap="md" wrap="nowrap" align="center" style={{ minWidth: 0 }}>
          {avatar ? (
            <Avatar radius="xl" size={40} color="sage" variant="outline">
              {String(badge || '?').charAt(0).toUpperCase()}
            </Avatar>
          ) : (
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                border: '2px solid var(--mantine-color-sage-6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Text fw={700} style={{ color: 'var(--mantine-color-sage-6)', fontSize: 18, lineHeight: 1 }}>
                {badge}
              </Text>
            </Box>
          )}
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }} truncate>
              {title}
            </Text>
            <Text size="xs" style={{ color: 'var(--aviary-muted)' }} truncate>
              {subtitle}
            </Text>
          </Stack>
        </Group>
        {ctaLabel && (
          <Button
            variant={highlight ? 'filled' : 'default'}
            color="sage"
            size="sm"
            rightSection={highlight ? <IconExternalLink size={14} /> : <IconArrowRight size={14} />}
            onClick={(e) => { e.stopPropagation(); onClick?.(); }}
            styles={{ root: { flexShrink: 0 } }}
          >
            {ctaLabel}
          </Button>
        )}
      </Group>
    </Paper>
  );
}

function formatAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!then) return '';
  const diff = Date.now() - then;
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  const hrs = Math.floor(diff / (60 * 60 * 1000));
  const mins = Math.floor(diff / (60 * 1000));
  if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  if (hrs > 0) return `${hrs}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'just now';
}

function isSameLocalDay(iso, reference) {
  if (!iso) return false;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return false;
  return date.getFullYear() === reference.getFullYear()
    && date.getMonth() === reference.getMonth()
    && date.getDate() === reference.getDate();
}

export default function TodayScreen({ principalName, onStartCampaign }) {
  const api = useApi();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchStats(api).catch(() => null),
      fetchLeads(api).catch(() => []),
      fetchEvents(api).catch(() => ({ events: [] })),
      fetchCampaigns(api).catch(() => []),
    ]).then(([statsData, leadData, eventData, campaignData]) => {
      if (cancelled) return;
      setStats(statsData || {});
      const leadsList = Array.isArray(leadData) ? leadData : (leadData?.leads || []);
      setLeads(leadsList);
      const eventsList = Array.isArray(eventData?.events) ? eventData.events : (Array.isArray(eventData) ? eventData : []);
      setEvents(eventsList);
      const campaignsList = Array.isArray(campaignData) ? campaignData : (campaignData?.campaigns || []);
      setCampaigns(campaignsList);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api]);

  // Active (non-archived, non-lost) leads only. Archived = skipped-in-approval-queue
  // leads that shouldn't clutter any dashboard number. Lost = closed-unsuccessfully.
  const activeLeads = useMemo(() => (
    leads.filter((l) => l.archived !== true && String(l.stage || '').toLowerCase() !== 'lost')
  ), [leads]);

  // Per-stage CURRENT counts (not cumulative). Matches the mock's 142/38/11/4 shape.
  // Folds v1's collapsed stages: qualified→contacted, hot/clicked→clicked, closed→booked.
  const funnelCounts = useMemo(() => {
    const buckets = { prospect: 0, contacted: 0, clicked: 0, booked: 0 };
    for (const l of activeLeads) {
      const s = String(l.stage || 'prospect').toLowerCase();
      if (s === 'contacted' || s === 'qualified') buckets.contacted += 1;
      else if (s === 'clicked' || s === 'hot') buckets.clicked += 1;
      else if (s === 'booked' || s === 'closed') buckets.booked += 1;
      else buckets.prospect += 1;
    }
    return buckets;
  }, [activeLeads]);

  // Header stats — all CURRENT stage counts. Conversion = booked ÷ (everyone who's
  // been reached out to). If nobody's been contacted yet, show "—".
  const headerStats = useMemo(() => {
    const inPipeline = activeLeads.length;
    const contacted = funnelCounts.contacted;
    const clicked = funnelCounts.clicked;
    const booked = funnelCounts.booked;
    const reached = contacted + clicked + booked;
    const conversion = reached > 0
      ? Number(((booked / reached) * 100).toFixed(1))
      : null;
    return { inPipeline, contacted, clicked, booked, conversion };
  }, [activeLeads, funnelCounts]);

  // "3 things need you today" computations. Archived leads (skipped) never appear.
  // A lead counts as waiting if EITHER the campaign opener is unapproved OR any
  // article-promo draft is still pending. Article-promo pushes any non-archived
  // lead (base warm / cold / etc) into the queue, not just prospect-stage rows.
  const waitingLeads = useMemo(() => (
    leads.filter((l) => {
      if (l.archived === true) return false;
      const needsOpenerApproval =
        String(l.stage || 'prospect').toLowerCase() === 'prospect' && !l.approvedAt;
      const pendingArticlePromo = Array.isArray(l.articleDrafts)
        && l.articleDrafts.some((d) => !d.approvedAt && !d.skippedAt);
      return needsOpenerApproval || pendingArticlePromo;
    })
  ), [leads]);
  const waitingCampaignIds = useMemo(() => new Set(waitingLeads.map((l) => String(l.campaignId || ''))), [waitingLeads]);
  const oldestWaiting = useMemo(() => {
    let oldest = null;
    for (const l of waitingLeads) {
      const ts = new Date(l.createdAt || 0).getTime();
      if (!Number.isNaN(ts) && (!oldest || ts < oldest)) oldest = ts;
    }
    return oldest ? new Date(oldest).toISOString() : null;
  }, [waitingLeads]);

  const overnightLeads = useMemo(() => {
    const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
    return leads.filter((l) => l.archived !== true && new Date(l.createdAt || 0).getTime() >= dayAgo);
  }, [leads]);
  const strongOvernight = useMemo(() => (
    overnightLeads.filter((l) => Number(l.fitScore ?? l.score ?? 0) >= 80).length
  ), [overnightLeads]);
  const overnightCampaign = useMemo(() => {
    if (!overnightLeads.length) return null;
    const cid = String(overnightLeads[0].campaignId || '');
    const found = campaigns.find((c) => String(c.id) === cid);
    return found?.name || null;
  }, [overnightLeads, campaigns]);

  const latestReply = useMemo(() => {
    return events.find((e) => String(e.type || '').toLowerCase() === 'replied') || null;
  }, [events]);

  const newConnectionsToday = useMemo(() => {
    const today = new Date();
    return activeLeads.filter((lead) => isSameLocalDay(lead.connectedAt, today)).length;
  }, [activeLeads]);

  const thingsCount = (waitingLeads.length > 0 ? 1 : 0)
    + (overnightLeads.length > 0 ? 1 : 0)
    + (latestReply ? 1 : 0);

  const greeting = principalName ? `Welcome back, ${principalName}.` : 'Welcome back.';

  if (loading) {
    return (
      <Center py={80}><Loader color="sage" /></Center>
    );
  }

  return (
    <Stack gap="xl" py={20}>
      <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28, lineHeight: 1.2 }}>
        {greeting}
      </Text>

      {newConnectionsToday > 0 && (
        <Paper p="sm" radius="md" withBorder style={{ background: 'var(--mantine-color-sage-0)' }}>
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text size="sm" fw={600} style={{ color: 'var(--mantine-color-sage-8)' }}>
              {newConnectionsToday} {newConnectionsToday === 1 ? 'new connection today' : 'new connections today'}
            </Text>
            <Button
              size="xs"
              variant="light"
              color="sage"
              onClick={() => navigate('/v2/board?lane=connected')}
            >
              Open Connected lane
            </Button>
          </Group>
        </Paper>
      )}

      {/* PIPELINE — stat row + funnel bar */}
      <Paper p="lg" radius="md" withBorder style={{ background: '#fff' }}>
        <Stack gap="md">
          <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
            YOUR PIPELINE RIGHT NOW
          </Text>
          <Group gap="lg" wrap="nowrap" mt={4}>
            <StatCell label="in pipeline" value={headerStats.inPipeline} />
            <StatCell label="contacted" value={headerStats.contacted} />
            <StatCell label="clicked" value={headerStats.clicked} />
            <StatCell label="booked" value={headerStats.booked} highlight />
            <StatCell label="conversion" value={headerStats.conversion === null ? '—' : `${headerStats.conversion}%`} />
          </Group>
          <Stack gap={6}>
            <FunnelBar counts={funnelCounts} />
            <Group justify="space-between">
              {FUNNEL_STAGES.map((s) => (
                <Text key={s.key} size="xs" style={{ color: 'var(--aviary-muted)' }}>
                  {s.label}
                </Text>
              ))}
            </Group>
          </Stack>
        </Stack>
      </Paper>

      {/* THINGS TO DO */}
      {thingsCount > 0 && (
        <Stack gap="md">
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
            {thingsCount} {thingsCount === 1 ? 'thing needs' : 'things need'} you today
          </Text>

          {waitingLeads.length > 0 && (
            <ActionRow
              badge={waitingLeads.length}
              title={waitingLeads.length === 1 ? 'Message waiting to send' : 'Messages waiting to send'}
              subtitle={`across ${waitingCampaignIds.size} ${waitingCampaignIds.size === 1 ? 'campaign' : 'campaigns'}${oldestWaiting ? ` · oldest queued ${formatAgo(oldestWaiting)}` : ''}`}
              ctaLabel="Review"
              onClick={() => navigate(`/v2/leads/${waitingLeads[0].id}?tab=message`, {
                state: { back: { path: '/v2', label: 'Today' } },
              })}
            />
          )}

          {overnightLeads.length > 0 && (
            <ActionRow
              badge={overnightLeads.length}
              title={overnightLeads.length === 1 ? 'New prospect found overnight' : 'New prospects found overnight'}
              subtitle={
                overnightCampaign
                  ? `${overnightCampaign}${strongOvernight > 0 ? ` · ${strongOvernight} look strong` : ''}`
                  : (strongOvernight > 0 ? `${strongOvernight} look strong` : 'across your campaigns')
              }
              ctaLabel="Review"
              onClick={() => {
                const cid = overnightLeads[0].campaignId;
                if (cid) navigate(`/v2/campaigns/${cid}`, {
                  state: { back: { path: '/v2', label: 'Today' } },
                });
                else navigate('/v2/campaigns');
              }}
            />
          )}

          {latestReply && (
            <ActionRow
              highlight
              avatar
              badge={latestReply.leadName || 'P'}
              title={`${latestReply.leadName || 'Someone'} replied${latestReply.channel ? ` on ${latestReply.channel}` : ''}`}
              subtitle={`${formatAgo(latestReply.ts || latestReply.createdAt)}${latestReply.summary ? ' — ' + latestReply.summary : ' — over to you'}`}
              ctaLabel="Open"
              onClick={() => {
                if (latestReply.leadId) navigate(`/v2/leads/${latestReply.leadId}`, {
                  state: { back: { path: '/v2', label: 'Today' } },
                });
              }}
            />
          )}
        </Stack>
      )}

      {/* FOOTER — quiet reassurance + start-a-new-campaign */}
      <Divider variant="dashed" mt="md" />
      <Group justify="space-between" align="center" wrap="wrap">
        <Text size="sm" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>
          Sent at a human pace, business hours, never twice.
        </Text>
        {onStartCampaign && (
          <Button color="sage" size="md" onClick={onStartCampaign}>
            Start a new campaign »
          </Button>
        )}
      </Group>

      {thingsCount === 0 && waitingLeads.length === 0 && overnightLeads.length === 0 && !latestReply && (
        <UnstyledButton onClick={() => navigate('/v2/campaigns')} style={{ alignSelf: 'flex-start' }}>
          <Text size="xs" c="sage">Nothing pending — check your active campaigns →</Text>
        </UnstyledButton>
      )}
    </Stack>
  );
}
